import { memo, useMemo, type JSX } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/context/store/store';
import { DocumentEditorBlock, DocumentViewBlock } from '@/components/document';
import { splitChildrenIntoClusters } from '@/components/document/cluster-split';
import { DocumentEditorParagraphCluster } from '@/components/document/document-editor-paragraph-cluster';
import { selectEditorRemountKey } from '@/context/store/slices/editorRemountSlice';
import type { DocumentEntity } from '@/context/store/slices/documentDataSlice';

const CLUSTER_PARENT_TYPES = new Set([
  'subsection',
  'section',
  'part',
  'rule',
  'article',
  'appendix',
  'definition',
]);

function isClusterParent(node: DocumentEntity): boolean {
  return node.type === 'container' && CLUSTER_PARENT_TYPES.has(node.style);
}

type DocumentNodeProps = {
  id: string;
  view: boolean;
  depth?: number;
  parentStyle?: string | null;
};

type ParagraphSibling = {
  id: string;
  type: 'text';
  style: 'body' | 'note' | 'example' | 'exception';
  order: number;
  text: string;
};

function buildChildNodes(
  node: DocumentEntity,
  childEntities: DocumentEntity[],
  view: boolean,
  parentDepth: number,
  getRemountKey: (ownerId: string) => string,
): JSX.Element[] {
  // Per-child depth: containers get parent_depth + 1; text/table/image
  // leaves inherit the parent container's depth (matches BE observation
  // recording — see pdf-structural-induction.ts comments).
  const depthForChild = (child: DocumentEntity): number => {
    return child.type === 'container' ? parentDepth + 1 : parentDepth;
  };

  const childParentStyle =
    node.type === 'container' ? node.style : null;

  if (view || !isClusterParent(node)) {
    return childEntities.map(child => (
      <DocumentEditorNode
        key={
          !view && child.type === 'list'
            ? `${child.id}-r${getRemountKey(child.id)}`
            : child.id
        }
        id={child.id}
        view={view}
        depth={depthForChild(child)}
        parentStyle={childParentStyle}
      />
    ));
  }

  return splitChildrenIntoClusters(childEntities).map((item, clusterIndex) => {
    if (item.kind === 'cluster') {
      // splitChildrenIntoClusters filters items to leaf text entities at
      // runtime (isLeafText), but the generic T isn't narrowed by TS — cast
      // each item to the text variant so we can read `style` and `text`.
      const siblings = item.items.map(raw => {
        const e = raw as DocumentEntity & { type: 'text' };
        return {
          id: e.id,
          type: 'text' as const,
          style: e.style,
          order: e.order,
          text: e.text ?? '',
        };
      }) satisfies ParagraphSibling[];
      // Stable cluster-owner key: parent id + the cluster's own first
      // sibling id, NOT the parent-relative `clusterIndex`. A structural
      // edit elsewhere under the same parent (e.g. inserting a table above
      // this cluster, or a sibling cluster gaining/losing items) shifts
      // every subsequent cluster's index in `splitChildrenIntoClusters`'
      // output, which would otherwise remount clusters that didn't
      // themselves change. Keying on the first sibling's id instead keeps
      // a cluster's identity stable across those unrelated shifts — React
      // reorders/updates it in place rather than remounting it. Structural
      // sync within a cluster is handled by the paragraph cluster's own
      // reconciliation effect, not by this key. The cluster's remount
      // owner is still the parent container (`node.id`) — see
      // `resolveEditorOwnerId` — so all paragraph/note/example/exception
      // clusters under it remount together when that owner is bumped.
      const firstSiblingId = siblings[0]?.id ?? `empty-${String(clusterIndex)}`;
      const clusterKey = `${node.id}-cluster-${firstSiblingId}-r${getRemountKey(node.id)}`;

      return (
        <DocumentEditorParagraphCluster
          key={clusterKey}
          parentId={node.id}
          siblings={siblings}
          depth={parentDepth}
        />
      );
    }
    return (
      <DocumentEditorNode
        key={
          !view && item.node.type === 'list'
            ? `${item.node.id}-r${getRemountKey(item.node.id)}`
            : item.node.id
        }
        id={item.node.id}
        view={view}
        depth={depthForChild(item.node)}
        parentStyle={childParentStyle}
      />
    );
  });
}

function DocumentEditorNodeComponent({
  id,
  view,
  depth = 0,
  parentStyle = null,
}: DocumentNodeProps) {
  const node = useSelector((state: RootState) => state.documentData.byId[id]);
  const childEntities = useSelector((state: RootState) => {
    const childIds = state.documentData.childrenById[id] ?? [];
    return childIds
      .map(childId => state.documentData.byId[childId])
      .filter((entity): entity is DocumentEntity => !!entity);
  }, shallowEqual);

  // Only the remount versions for owners that actually matter to THIS
  // node's children (its own id — the cluster owner — plus any list
  // child's own id) are selected, and `shallowEqual` bails the re-render
  // when none of them changed. A bump for an unrelated owner elsewhere in
  // the document never touches this selector's output.
  const relevantOwnerIds = useMemo(() => {
    const ids = new Set<string>([id]);
    if (!view) {
      for (const child of childEntities) {
        if (child.type === 'list') ids.add(child.id);
      }
    }
    return [...ids];
  }, [id, childEntities, view]);

  const remountVersions = useSelector((state: RootState) => {
    const map: Record<string, string> = {};
    for (const ownerId of relevantOwnerIds) {
      map[ownerId] = selectEditorRemountKey(state.editorRemount, ownerId);
    }
    return map;
  }, shallowEqual);

  const getRemountKey = useMemo(
    () => (ownerId: string) => remountVersions[ownerId] ?? '0:0',
    [remountVersions],
  );

  const childNodes = useMemo(() => {
    if (!node) return [];
    return buildChildNodes(node, childEntities, view, depth, getRemountKey);
  }, [node, childEntities, view, depth, getRemountKey]);

  if (!node) return null;

  if (view) {
    return (
      <DocumentViewBlock
        node={node}
        childNodes={childNodes}
        depth={depth}
        parentStyle={parentStyle}
      />
    );
  }

  return (
    <DocumentEditorBlock
      node={node}
      childNodes={childNodes}
      depth={depth}
      parentStyle={parentStyle}
    />
  );
}

const DocumentEditorNode = memo(DocumentEditorNodeComponent);

export { DocumentEditorNode };
